import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type CurrentUser, changePassword } from '../api.ts';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { errorMessage } from '../messages.ts';
import { UserIcon } from './AvatarIcons.tsx';
import { LocalePicker } from './LocalePicker.tsx';
import { ThemePicker } from './ThemePicker.tsx';

/**
 * Il menu del profilo: dove sono finite lingua, aspetto e uscita.
 *
 * Prima quei tre controlli stavano aperti nella barra, su **ogni** schermata:
 * due menu a tendina con le etichette "Lingua" e "Aspetto" in grassetto e un
 * pulsante "Esci", cioè tre dei dieci controlli permanenti di una console la
 * cui schermata principale ne ha cinque. Erano anche gli elementi più larghi
 * della barra, e sulla schermata di accesso stavano **accanto al titolo**,
 * prima del campo email: la prima cosa che si vedeva del prodotto era un
 * selettore di tema.
 *
 * Lingua e aspetto si scelgono una volta e poi non si toccano più per mesi.
 * Restano raggiungibili da qualunque schermata in due gesti — che è quello che
 * serve a chi si ritrova la console nella lingua sbagliata — senza occupare
 * spazio in quelle in cui nessuno le sta cercando.
 *
 * **Non è un `role="menu"`.** Dentro ci sono due `<select>` veri e un pulsante:
 * un menu ARIA li governerebbe con le frecce, togliendo alle tendine il
 * comportamento che hanno già e che chi usa uno screen reader si aspetta. È
 * una divulgazione — un pulsante con `aria-expanded` e un pannello subito dopo
 * nell'ordine del documento — che è il modello che la WAI indica per questo
 * caso preciso.
 */
type Props = {
  user: CurrentUser;
  onSignedOut: () => void;
};

/**
 * Cambiare la propria password, da qui — l'unico posto raggiungibile da
 * loggato, per qualunque ruolo (`docs/DECISIONI.md`).
 *
 * Fino a oggi solo due schermate toccavano una password: l'invito, la prima
 * volta (`InvitePage.tsx`), e il reset da sloggato (`PasswordResetPage.tsx`,
 * BB8). Chi era già dentro non aveva alcun modo di cambiarla — nemmeno il
 * fornitore, il primo cui viene mai assegnata anziché scelta (l'unico
 * account che non nasce da un invito, perché non esiste nessuno che possa
 * invitare il primissimo).
 *
 * **Non in assistenza**: una sessione impersonata è in sola lettura (sezione
 * 9bis) — il server rifiuterebbe comunque, stessa guardia di
 * `/me/locale` — e cambiare "la propria" password durante un giro dentro
 * l'ente di qualcun altro non avrebbe nemmeno senso: il soggetto della
 * sessione resta il fornitore, non l'ente che sta guardando.
 *
 * Un solo campo per la password nuova, senza "ripetila": stessa scelta di
 * `InvitePage.tsx`, un errore di battitura si scopre comunque al primo
 * accesso fallito.
 */
function PasswordChangeFieldset() {
  const { t } = useTranslation();
  const { say, announcer } = useAnnounce();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const currentId = useId();
  const newId = useId();
  const hintId = useId();

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      say(t('account.password_changed'));
    } catch (error) {
      say(errorMessage(t, error), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="usermenu__password" onSubmit={(event) => void submit(event)}>
      <h3>{t('account.password_title')}</h3>
      {announcer}

      <div className="field">
        <label htmlFor={currentId}>{t('account.current_password_label')}</label>
        <input
          id={currentId}
          type="password"
          autoComplete="current-password"
          required
          maxLength={200}
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={newId}>{t('account.new_password_label')}</label>
        <input
          id={newId}
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          maxLength={200}
          value={newPassword}
          aria-describedby={hintId}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <p className="muted" id={hintId}>
          {t('account.new_password_hint')}
        </p>
      </div>

      <button
        type="submit"
        className="button"
        disabled={busy || !currentPassword || newPassword.length < 12}
      >
        {t('account.password_save')}
      </button>
    </form>
  );
}

export function AccountMenu({ user, onSignedOut }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  /**
   * Si chiude toccando altrove, e con `Esc`.
   *
   * Sono i due gesti che chiunque prova per primo davanti a un pannello
   * aperto. Con `Esc` il fuoco torna sul pulsante che l'ha aperto: senza,
   * cadrebbe su `body` e chi naviga da tastiera dovrebbe ritabulare la barra
   * dall'inizio per tornare dov'era.
   *
   * L'ascolto sta su `pointerdown` e non su `click`: un `click` arriva **dopo**
   * che il bersaglio ha già reagito, e premendo una voce di navigazione mentre
   * il pannello è aperto si sarebbe vista la sezione cambiare con il pannello
   * ancora sopra.
   */
  useEffect(() => {
    if (!open) return;

    const closeFromOutside = (event: Event): void => {
      const target = event.target;
      if (target instanceof Node && container.current?.contains(target)) return;
      setOpen(false);
    };

    const closeFromKeyboard = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };

    /*
     * E si chiude anche **tabulando via**.
     *
     * Senza, il pannello restava aperto dietro il contenuto mentre il fuoco
     * era gia' tre controlli piu' avanti: chi guarda lo schermo lo vede
     * sospeso li', e chi non lo guarda si ritrova due tendine di preferenze
     * in mezzo alla navigazione. `focusin` sul documento dice **dove il fuoco
     * e' arrivato**, che e' l'unico modo di sapere che ha lasciato tutto il
     * gruppo e non e' solo passato da un controllo all'altro qui dentro.
     */
    document.addEventListener('pointerdown', closeFromOutside);
    document.addEventListener('keydown', closeFromKeyboard);
    document.addEventListener('focusin', closeFromOutside);
    return () => {
      document.removeEventListener('pointerdown', closeFromOutside);
      document.removeEventListener('keydown', closeFromKeyboard);
      document.removeEventListener('focusin', closeFromOutside);
    };
  }, [open]);

  return (
    <div className="usermenu" ref={container}>
      <button
        type="button"
        className="usermenu__trigger"
        ref={trigger}
        aria-expanded={open}
        aria-controls={panelId}
        /*
          Il nome accessibile è esplicito perché su un telefono il nome scritto
          non c'è: sotto i 544px resta il solo dischetto, che è `aria-hidden`.
          Senza questa etichetta il pulsante sarebbe senza nome proprio sul
          dispositivo su cui la console si usa di più.
        */
        aria-label={t('account.menu')}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="avatar" aria-hidden="true">
          <UserIcon />
        </span>
        <span className="usermenu__name">{user.displayName}</span>
      </button>

      {open ? (
        <div className="usermenu__panel" id={panelId}>
          <p className="usermenu__who">
            <strong>{user.displayName}</strong>
            <span>{user.email}</span>
          </p>

          {/*
            Qui c'è un profilo su cui salvare la lingua, e nelle due schermate
            senza accesso no: la scelta si ricorda anche sul profilo, per il
            dispositivo successivo (AS6).

            **Tranne in assistenza.** Lì il server rifiuta — una sessione
            impersonata è in sola lettura — e chiedere lo stesso vorrebbe dire
            un 403 a ogni cambio di lingua, inghiottito da un `catch`.
          */}
          <LocalePicker signedIn={!user.impersonation} />
          <ThemePicker />

          {/*
            Non in assistenza: si veda il commento in cima a
            `PasswordChangeFieldset` — stessa ragione di `LocalePicker` due
            righe sopra, il server rifiuterebbe comunque.
          */}
          {!user.impersonation ? <PasswordChangeFieldset /> : null}

          <div className="usermenu__exit">
            <button type="button" className="button button--wide" onClick={onSignedOut}>
              {t('home.logout')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
